export interface GameTrackingData {
  gameDateIso: string;
  competitors?: Array<{ name: string }>;
  venue?: {
    name: string;
  };
}

type TrackingEvent = 'broadcasting_watch' | 'ticket_start_cart' | 'add_to_calendar';
type CalendarPlatform = 'Google' | 'Apple' | 'Office';

// Define interfaces for our dataLayer objects
interface BaseDataLayerObject {
  event: string;
  game_info: {
    teams: string;
    stadium: string;
    date: string;
    time: string;
  };
}

interface BroadcastWatchDataLayerObject extends BaseDataLayerObject {
  broadcaster: string;
}

interface AddToCalendarDataLayerObject extends BaseDataLayerObject {
  calendar_platform: CalendarPlatform;
}

export function trackGameEvent(
  eventName: TrackingEvent, 
  gameData: GameTrackingData,
  extraData?: { calendarPlatform?: CalendarPlatform }
) {
  if (!window.dataLayer) {
    console.warn('dataLayer not found, skipping event tracking');
    return;
  }

  try {
    const gameDate = gameData.gameDateIso 
      ? new Date(gameData.gameDateIso)
      : null;
    
    const formattedDate = gameDate
      ? gameDate.toLocaleDateString('en-US', { 
          month: 'long',
          day: 'numeric', 
          year: 'numeric'
        })
      : '';
      
    const formattedTime = gameDate
      ? gameDate.toLocaleTimeString('en-US', { 
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'America/New_York'
        }) + ' ET'
      : '';

    // Ensure competitors are properly defined
    const awayTeam = gameData.competitors && gameData.competitors.length > 0 ? gameData.competitors[0]?.name || '' : '';
    const homeTeam = gameData.competitors && gameData.competitors.length > 1 ? gameData.competitors[1]?.name || '' : '';
    const gameTeams = `${homeTeam} vs ${awayTeam}`.trim();
    
    // Base game info object
    const gameInfo = {
      teams: gameTeams,
      stadium: gameData.venue?.name || '',
      date: formattedDate,
      time: formattedTime
    };
    
    // Create dataLayer object based on event type
    let dataLayerObject: BaseDataLayerObject | BroadcastWatchDataLayerObject | AddToCalendarDataLayerObject;
    
    // Simple non-ecommerce event format
    if (eventName === 'broadcasting_watch') {
      dataLayerObject = {
        event: eventName,
        game_info: gameInfo,
        broadcaster: 'ESPN'
      };
    } 
    else if (eventName === 'add_to_calendar' && extraData?.calendarPlatform) {
      dataLayerObject = {
        event: eventName,
        game_info: gameInfo,
        calendar_platform: extraData.calendarPlatform
      };
    }
    // E-commerce event format for ticket cart
    else {
      dataLayerObject = {
        event: 'begin_checkout',
        game_info: gameInfo
      };
    }
    
    // For debugging
    // console.log('Pushing to dataLayer:', JSON.stringify(dataLayerObject));
    
    // Safe push to dataLayer
    if (dataLayerObject) {
      window.dataLayer.push(dataLayerObject);
    }
  } catch (error) {
    // console.error('Error in trackGameEvent:', error);
  }
}

// Convenience functions
export const trackBroadcastWatch = (gameData: GameTrackingData) => 
  trackGameEvent('broadcasting_watch', gameData);

export const trackTicketStartCart = (gameData: GameTrackingData) => 
  trackGameEvent('ticket_start_cart', gameData);

export const trackAddToCalendar = (gameData: GameTrackingData, platform: CalendarPlatform) => 
  trackGameEvent('add_to_calendar', gameData, { calendarPlatform: platform }); 